"use client";

import { Leaf, Lightbulb, Handshake, Check } from "@/components/icons";
import * as toneStyles from "@/components/ui/tone";
import { cn } from "@/lib/utils";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAboutContext } from "../context/about-context";
import { isOneOf } from "@shared/validation/is-one-of";

const VALUES = [
  {
 id: "sustainability" as const,
 label: "Sustainability",
 icon: Leaf,
 color: "success",
 headline: "Environmental First",
 description:
      "Environmental responsibility is at the core of everything we do. We believe that business success and environmental stewardship go hand in hand.",
 examples: [
      "Circular economy focus",
      "Waste reduction at scale",
      "Carbon footprint tracking",
      "Zero-landfill initiatives",
    ],
  },
  {
 id: "innovation" as const,
 label: "Innovation",
 icon: Lightbulb,
 color: "info",
 headline: "Always Improving",
 description:
      "We constantly push boundaries to create better packaging solutions. Technology enables us to match supply with demand faster than ever before.",
 examples: [
      "AI-powered matching",
      "Smart logistics optimization",
      "Real-time market pricing",
      "Predictive inventory tools",
    ],
  },
  {
 id: "partnership" as const,
 label: "Partnership",
 icon: Handshake,
 color: "primary",
 headline: "Growing Together",
 description:
      "We build lasting relationships with our clients and suppliers. Your success is our success, and we invest in every relationship for the long term.",
 examples: [
      "Dedicated account support",
      "Flexible payment terms",
      "Volume pricing programs",
      "Custom logistics solutions",
    ],
  },
] as const;

const VALUE_IDS = VALUES.map((value) => value.id);

const colorClasses = {
 success: {
 icon: toneStyles.TONE_TEXT.success,
 bg: toneStyles.TONE_BACKGROUND.success,
 border: toneStyles.TONE_BORDER.success,
 bullet: toneStyles.TONE_DOT.success,
  },
 info: {
 icon: toneStyles.TONE_TEXT.info,
 bg: toneStyles.TONE_BACKGROUND.info,
 border: toneStyles.TONE_BORDER.info,
 bullet: toneStyles.TONE_DOT.info,
  },
 primary: {
 icon: toneStyles.TONE_TEXT.brand,
 bg: toneStyles.TONE_BACKGROUND.brand,
 border: "border-primary/20",
 bullet: "bg-action-fill",
  },
} as const;

type AboutValue = (typeof VALUES)[number];
type ValueColors = (typeof colorClasses)[keyof typeof colorClasses];

interface ValueTabPartProps {
  value: AboutValue;
  colors: ValueColors;
}

function ValueSummaryPanel({ value, colors }: ValueTabPartProps) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-2xl p-8 lg:p-10",
        colors.bg,
        "border",
        colors.border,
      )}
    >
      <div
        className={cn(
          "mb-6 inline-flex h-16 w-16 items-center justify-center rounded-2xl",
          "bg-card/80",
          colors.border,
          "border",
        )}
      >
        <value.icon
          className={cn("h-8 w-8", colors.icon)}
          strokeWidth={1.5}
        />
      </div>

      <h3 className="mb-3 text-2xl font-bold text-foreground lg:text-3xl">
        {value.headline}
      </h3>

      <p className="text-lg leading-relaxed text-muted-foreground">
        {value.description}
      </p>
    </div>
  );
}

function ValueExampleList({ value, colors }: ValueTabPartProps) {
  return (
    <div className="space-y-4">
      {value.examples.map((example, index) => (
        <div
          key={index}
          className={cn(
            "flex items-center gap-4 rounded-xl bg-card/80 p-4",
            "border border-border/50",
            "duration-200",
          )}
        >
          <div
            className={cn(
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
              colors.bg,
            )}
          >
            <Check
              className={cn("h-4 w-4", colors.icon)}
              strokeWidth={2.5}
            />
          </div>
          <span className="font-medium text-foreground">{example}</span>
        </div>
      ))}
    </div>
  );
}

function ValueTabContent({ value, colors }: ValueTabPartProps) {
  return (
    <TabsContent
      value={value.id}
      className="mt-0 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-right-4 data-[state=active]:duration-300"
    >
      <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
        <ValueSummaryPanel value={value} colors={colors} />
        <ValueExampleList value={value} colors={colors} />
      </div>
    </TabsContent>
  );
}

function ValuesHeader() {
  return (
    <div className="mb-12 text-center">
      <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-success-bg px-4 py-1.5 text-sm font-medium text-success-strong">
        <Leaf className="h-4 w-4" />
        Our Values
      </div>
      <h2 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl lg:text-5xl">
        What drives us forward
      </h2>
    </div>
  );
}

function ValuesTabTriggers() {
  return (
    <div className="mb-8 flex justify-center">
      <TabsList className="h-auto gap-1 rounded-xl border border-border/50 bg-muted/50 p-1.5">
        {VALUES.map((value) => (
          <TabsTrigger
            key={value.id}
            value={value.id}
            className={cn(
              "flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-all duration-200",
              "data-[state=active]:bg-foreground data-[state=active]:text-background data-[state=active]:shadow-lg",
              "data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:bg-muted data-[state=inactive]:hover:text-foreground",
            )}
          >
            <value.icon className="h-4 w-4" />
            <span className="hidden sm:inline">{value.label}</span>
          </TabsTrigger>
        ))}
      </TabsList>
    </div>
  );
}

export function ValuesSection() {
  const { activeValuesTab, setActiveValuesTab } = useAboutContext();

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4">
        <ValuesHeader />

        <Tabs
          value={activeValuesTab}
          onValueChange={(nextTab) => isOneOf(VALUE_IDS, nextTab) && setActiveValuesTab(nextTab)}
          className="w-full"
        >
          <ValuesTabTriggers />

          <div className="relative min-h-[400px]">
            {VALUES.map((value) => (
              <ValueTabContent
                key={value.id}
                value={value}
                colors={colorClasses[value.color]}
              />
            ))}
          </div>
        </Tabs>
      </div>
    </section>
  );
}
